<template>
  <app-layout>
    <template #header>
      <inertia-link
        :href="route('dashboard')"
        class="lg:absolute relative md:left-7 flex items-center text-vice-teal-500 text-xs"
      >
        <ChevronLeftIcon class="w-4 h-4 mr-1"></ChevronLeftIcon>
        <span class="flex-none">Dashboard</span>
      </inertia-link>

      <!-- Header -->
      <div class="pb-5 pt-4 2xl:pt-0 sm:flex sm:items-center sm:justify-between">
        <div class="flex gap-x-6 justify-between items-center">
          <!-- Header icon -->
          <div class="flex items-center">
            <div class="block rounded-3xl bg-vice-teal-50 p-1">
              <ViceStethoscope pathFill="#0D9488" class="h-20 w-20" />
            </div>
          </div>

          <!-- Header middle -->
          <div class="flex flex-col">
            <div class="flex">
              <h2 class="text-2xl max-w-3xl text-vice-teal-900 font-semibold leading-tight">
                {{ this.$page.props.course.title }}
              </h2>
            </div>
            <div class="flex gap-6 pt-3">
              <p
                v-if="this.$page.props.course.delivery_type !== 'on_demand'"
                class="flex gap-1 items-center"
              >
                <CalendarIcon class="h-5 w-5 text-gray-400" />
                <span class="text-gray-900 text-xs">
                  {{ yearFormatter(this.$page.props.course.live_start_at) }}
                </span>
                -
                <span class="text-gray-900 text-xs">
                  {{ yearFormatter(this.$page.props.course.live_end_at) }}
                </span>
              </p>
              <p class="flex gap-1 items-center" v-if="this.$page.props.course.students_count">
                <UsersIcon class="h-5 w-5 text-gray-400" />
                <span class="text-gray-900 text-xs">
                  {{ this.$page.props.course.students_count }}
                  <span>
                    {{ this.$page.props.course.students_count > 1 ? 'students' : 'student' }}
                  </span>
                </span>
              </p>
              <p class="flex gap-1 items-center" v-if="this.$page.props.course.sessions_count">
                <BookOpenIcon class="h-5 w-5 text-gray-400" />
                <span class="text-gray-900 text-xs">
                  {{ this.$page.props.course.sessions_count }}
                  <span>
                    {{ this.$page.props.course.sessions_count > 1 ? 'sessions' : 'session' }}
                  </span>
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div class="max-w-6xl mx-auto pt-6 pb-10 px-4 sm:px-8">
      <!-- Progress bar -->
      <div
        v-if="!this.$page.props.isClinician"
        class="flex flex-col gap-3 p-4 bg-white shadow border border-gray-200 rounded-lg"
      >
        <div class="text-sm flex justify-between">
          <span class="font-semibold">Your progress</span>
          <span class="text-gray-600">{{ Math.round(this.$page.props.progress) }}%</span>
        </div>
        <div class="flex w-full bg-vice-teal-50 rounded-full h-2.5">
          <div
            class="bg-vice-teal-600 h-2.5 rounded-full"
            :style="{
              width: `${Math.round(this.$page.props.progress)}%`,
            }"
          />
        </div>
      </div>

      <!--  Session started alert  -->
      <SessionIsReadyToJoinAlert
        v-if="alertSession && !this.$page.props.isClinician"
        :session="alertSession"
      />

      <!--  Course overview  -->
      <div class="mx-auto grid grid-cols-3 gap-8 pt-6">
        <!-- General info  -->
        <div class="col-span-10 sm:col-span-2 space-y-8">
          <!-- About -->
          <div>
            <div class="flex">
              <span class="font-semibold mb-3">About this course</span>
            </div>
            <p class="text-gray-500 whitespace-pre-wrap">
              {{ this.$page.props.course.summary }}
            </p>
          </div>

          <!-- Background -->
          <div v-if="this.$page.props.course.background">
            <div class="flex">
              <span class="font-semibold mb-3">Background</span>
            </div>
            <p class="text-gray-500 whitespace-pre-wrap">
              {{ this.$page.props.course.background }}
            </p>
          </div>

          <!-- Learning Outcomes -->
          <div v-if="this.$page.props.course.learning_outcomes?.length > 0">
            <div class="flex">
              <span v-if="this.$page.props.isClinician" class="font-semibold mb-3">
                Learning outcomes
              </span>
              <span v-else class="font-semibold mb-3">What you will learn</span>
            </div>
            <ul class="text-gray-500 space-y-2">
              <li
                v-for="(outcome, index) in this.$page.props.course.learning_outcomes"
                :key="index"
                class="flex leading-relaxed items-center"
              >
                <CheckCircleIcon class="h-5 w-5 inline-block mr-2 text-vice-teal-500" />
                {{ outcome }}
              </li>
            </ul>
          </div>

          <!-- Sessions within course -->
          <div class="col-span-10 sm:col-span-2">
            <div class="relative my-5">
              <hr class="w-full text-gray-200" />
            </div>
            <div class="flex">
              <span class="font-semibold mb-3 mt-1">Your sessions in this course</span>
            </div>

            <!-- Sessions Card -->
            <div class="">
              <ul
                v-if="this.$page.props.course.sessions && this.$page.props.course.sessions.length"
                class="space-y-4"
              >
                <li
                  v-for="s in this.$page.props.course.sessions"
                  :key="s.id"
                  class="
                    py-3
                    px-4
                    bg-white
                    flex-col
                    items-center
                    border
                    shadow
                    overflow-hidden
                    sm:rounded-lg
                  "
                >
                  <span class="justify-between w-full flex items-center">
                    <span class="flex flex-col">
                      <!-- Title & Badge -->
                      <span class="inline-flex">
                        <span class="font-medium">{{ s.title }}</span>
                        <span
                          class="
                            border border-vice-teal-600
                            text-vice-teal-600
                            content-center
                            font-semibold
                            rounded-full
                            text-xxs
                            ml-3
                            px-2
                            py-0
                          "
                        >
                          {{ s.finished_at ? 'Completed' : 'Upcoming' }}
                        </span>
                      </span>

                      <!-- Date, Time & Duration -->
                      <span class="text-xs text-gray-500">
                        <span class="flex gap-2 my-2 items-center">
                          <CalendarIcon class="h-4 w-4 text-gray-300" />
                          <span class="text-gray-500 text-xxs">
                            {{ yearFormatter(s.details.session_start_by_timezone) }}
                          </span>
                          <ClockIcon class="h-4 w-4 text-gray-300" />
                          <span class="text-gray-500 text-xxs">
                            {{ hoursFormatter(s.details.session_start_by_timezone) }}
                          </span>
                          <span class="text-gray-500 text-xxs ml-1">
                            {{ s.details.duration }} min
                          </span>
                        </span>
                      </span>
                    </span>

                    <!-- Reflection -->
                    <span
                      v-if="s.reflections.length"
                      class="
                        text-vice-teal-600
                        bg-vice-teal-50
                        font-semibold
                        flex flex-col
                        rounded-full
                        text-xxs
                        py-1
                        px-3
                      "
                    >
                      Reflection: Submitted
                    </span>
                  </span>

                  <span class="relative w-full my-5">
                    <hr class="w-full text-gray-200" />
                  </span>

                  <!-- Invitees & Clinician -->
                  <span class="inline-flex gap-3 mt-2 items-center text-xxs text-gray-500">
                    <!-- Invitees -->
                    <span class="flex items-center">
                      {{ s.invites?.length }}
                      <UsersIcon class="h-4 w-4 ml-1 text-gray-300" />
                    </span>

                    <!-- Clinician -->
                    <span v-if="s.details?.clinician" class="flex items-center">
                      <ViceAvatar
                        :image="s.details?.clinician?.profile_photo_url"
                        :name="s.details?.clinician?.name"
                        class="w-7 h-7"
                      />
                      <span class="ml-1 flex items-center text-xs">
                        {{ s.details?.clinician?.name }}
                      </span>
                    </span>
                  </span>
                </li>
              </ul>
              <div v-else class="text-center py-6 text-gray-500 text-xs italic">
                No sessions scheduled.
              </div>
            </div>
          </div>
        </div>

        <!-- Right side  -->
        <div class="col-span-1 flex flex-col gap-6">
          <!-- Enter course -->
          <EnterCourseBox v-if="!this.$page.props.isClinician" />

          <!-- Clinicians -->
          <AboutCourseClinician v-if="$page.props.course.clinicians.length" />

          <!-- OverviewBox -->
          <CourseOverviewBox />
        </div>
      </div>
    </div>
  </app-layout>
</template>

<script>
import dayjs from 'dayjs';
import { ChevronLeftIcon } from '@heroicons/vue/solid';
import {
  CalendarIcon,
  CheckCircleIcon,
  BookOpenIcon,
  UsersIcon,
  ClockIcon,
} from '@heroicons/vue/outline';
import DateFormatMixin from '@/Mixins/DateFormatMixin.vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import ViceStethoscope from '@/Components/Icons/ViceStethoscope.vue';
import AboutCourseClinician from '@/Pages/User/Course/AboutCourseClinicians.vue';
import CourseOverviewBox from '@/Pages/User/Course/CourseOverviewBox.vue';
import ViceAvatar from '@/Components/Avatars/ViceAvatar.vue';
import EnterCourseBox from '@/Pages/User/Course/EnterCourseBox.vue';
import SessionIsReadyToJoinAlert from '@/Components/Alerts/SessionIsReadyToJoinAlert.vue';

export default {
  name: 'ShowCourseDetails',
  mounted() {},
  setup() {
    return {};
  },
  mixins: [DateFormatMixin],
  components: {
    SessionIsReadyToJoinAlert,
    EnterCourseBox,
    ViceAvatar,
    ClockIcon,
    CourseOverviewBox,
    AboutCourseClinician,
    ViceStethoscope,
    AppLayout,
    CheckCircleIcon,
    ChevronLeftIcon,
    CalendarIcon,
    BookOpenIcon,
    UsersIcon,
  },
  data() {
    return {
      alertSession: this.$page.props.joinSessionAlert,
    };
  },
  computed: {},
  methods: {
    formatDate(date) {
      return dayjs(date).fromNow();
    },

    getInitials(name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
  },
};
</script>

<style scoped>
.prose {
  color: #374151;
}

.prose :deep(p) {
  margin-bottom: 0.5rem;
}

.prose :deep(ul),
.prose :deep(ol) {
  margin-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.prose :deep(li) {
  margin-bottom: 0.25rem;
}

.raindrop {
  border-radius: 50% 50% 50% 0;
}

.bg-player-grey {
  background-color: #1f2937;
}

.v-enter-active {
  transition: opacity 0.5s ease;
}

.v-leave-active {
  transition: opacity 1s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
